import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Body, Button, PoppaysLoader} from '@lightricks/react-design-system';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {Brand} from '@/types/brand';
import {Lever} from '@/types/lever';
import useOrganizationBrandsQuery from '@/hooks/queries/use-organization-brands-query';
import useSubscriptionQuery from '@/hooks/queries/use-subscription-query';
import useBrandId from '@/hooks/use-brand-id';
import useSubscriptionLevers from '@/hooks/use-subscription-levers';
import styles from './BrandsView.module.scss';
import BrandListItem from './components/brand-list-item/BrandListItem';
import ConfigureBrandModal from './components/configure-brand-modal/ConfigureBrandModal';
import CreateBrandModal from './components/create-brand-modal/CreateBrandModal';

function BrandsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const brandId = useBrandId();

  useEffect(() => {
    const toast = searchParams.get('toast');
    const message = searchParams.get('message');
    const text = toast || message;
    if (text) {
      raiseFlashMessage({
        status: message ? 'error' : 'success',
        message: text,
      });
      searchParams.delete('toast');
      searchParams.delete('message');
      setSearchParams(searchParams, {replace: true});
    }
  }, [searchParams, setSearchParams]);

  const {brands, agencyId, isLoading} = useOrganizationBrandsQuery({
    brandId,
    enabled: !!brandId,
  });

  const {subscription} = useSubscriptionQuery({brandId: brandId ?? undefined});
  const levers = useSubscriptionLevers();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [configuringBrand, setConfiguringBrand] = useState<Brand | null>(null);

  const brandLever = (subscription?.levers as Lever[] | undefined)?.find(
    (l) => l.type === 'BrandCount'
  );

  const isLeverMaxed = !!levers?.isMaxed('BrandCount');

  if (isLoading) {
    return <PoppaysLoader absolute />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {brandLever && (
          <Body size="md" data-test-org-settings-brand-levers>
            {translate('{brandsUsed}/{brandsThreshold} brands used', {
              brandsUsed: brandLever.usedCount,
              brandsThreshold: brandLever.thresholdCount,
            })}
          </Body>
        )}
        <Button
          appearance="brand"
          mode="filled"
          size="medium"
          disabled={isLeverMaxed}
          onClick={() => setShowCreateModal(true)}
          data-test-org-settings-new-brand
        >
          {translate('Create New Brand')}
        </Button>
      </div>

      <div className={styles.brandList}>
        {brands.map((brand: Brand) => (
          <BrandListItem
            key={brand.id}
            brand={brand}
            onConfigure={() => setConfiguringBrand(brand)}
          />
        ))}
      </div>

      {showCreateModal && agencyId && (
        <CreateBrandModal
          agencyId={agencyId}
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {configuringBrand && agencyId && (
        <ConfigureBrandModal
          brand={configuringBrand}
          agencyId={agencyId}
          isOpen={!!configuringBrand}
          onClose={() => setConfiguringBrand(null)}
        />
      )}
    </div>
  );
}

export default BrandsView;
